import { useEffect, useRef, useState, useCallback } from 'react';
import i18n from 'i18next';
import { MapProps } from './types';
import {
    markerContentHtml, destinationTooltipHtml, memberTooltipHtml, myLocationHtml,
} from './markerStyle';
import { useUserLocation } from 'hooks/useUserLocation';
import { IcPin } from 'components/common/icons/EmojiIcons';
import config from 'config/config';
import ctl from './mapControls.module.css';

/**
 * 카카오맵 SDK 초기화
 * - index.html의 정적 스크립트 로드를 우선 시도
 * - 실패 시 동적으로 스크립트를 삽입하여 재시도
 */
let kakaoLoadPromise: Promise<void> | null = null;

const KAKAO_APP_KEY = config.KAKAO_MAP_KEY;

const loadKakaoScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
        // 이미 로드됨
        if (window.kakao?.maps) {
            resolve();
            return;
        }

        // 동적 스크립트 삽입
        if (!KAKAO_APP_KEY) {
            reject(new Error('카카오맵 API 키가 설정되지 않았습니다. REACT_APP_KAKAO_MAP_KEY 환경변수를 확인해주세요.'));
            return;
        }

        const script = document.createElement('script');
        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${KAKAO_APP_KEY}&autoload=false&libraries=services`;
        script.onload = () => {
            if (window.kakao?.maps) {
                resolve();
            } else {
                reject(new Error('카카오맵 SDK 스크립트가 로드되었지만 초기화에 실패했습니다. API 키를 확인해주세요.'));
            }
        };
        script.onerror = () => {
            reject(new Error('카카오맵 SDK 스크립트를 불러올 수 없습니다. 네트워크 연결을 확인해주세요.'));
        };
        document.head.appendChild(script);
    });
};

export const loadKakaoSDK = (): Promise<void> => {
    if (kakaoLoadPromise) return kakaoLoadPromise;

    kakaoLoadPromise = loadKakaoScript()
        .then(() => {
            return new Promise<void>((resolve) => {
                window.kakao.maps.load(() => {
                    console.log('[KakaoMap] SDK initialized');
                    resolve();
                });
            });
        })
        .catch((e) => {
            kakaoLoadPromise = null;
            throw e;
        });

    return kakaoLoadPromise;
};

export const KakaoMap: React.FC<MapProps> = ({ center, markers, routes, height = 300, zoomLevel = 4, showMyLocationBtn, fitBounds, focusTarget, viewAllTrigger }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<kakao.maps.Map | null>(null);
    // id → { main, tooltip, contentHash } 맵 — 재사용으로 setPosition 만 호출해 부드러운 이동 보장
    const markerMapRef = useRef<Map<string | number, {
        main: kakao.maps.Marker | kakao.maps.CustomOverlay;
        tooltip?: kakao.maps.CustomOverlay;
        contentHash: string;
    }>>(new Map());
    const polylinesRef = useRef<any[]>([]);
    const myLocMarkerRef = useRef<kakao.maps.CustomOverlay | null>(null);
    // 최신 markers 를 ref 로 유지 — 주기 auto-fit 루프가 참조
    const latestMarkersRef = useRef(markers);
    // 사용자 제스처 감지 시 auto-fit 5초간 mute
    const userInteractingUntilRef = useRef<number>(0);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { fetchLocation } = useUserLocation();

    // 두 손가락 터치 회전 — 카카오맵 자체 미지원이라 wrapper 에 CSS transform 적용.
    // 핀치 줌 등과 충돌 방지를 위해 회전 인정 임계값(ROTATE_THRESHOLD_DEG) 통과 후에만 적용.
    const ROTATE_THRESHOLD_DEG = 15;
    const [rotation, setRotation] = useState(0);
    const [rotating, setRotating] = useState(false);
    const rotateBaseRef = useRef<{
        startAngle: number;
        baseRotation: number;
        activated: boolean;
    } | null>(null);

    const calcAngle = (touches: React.TouchList): number => {
        const dx = touches[1].clientX - touches[0].clientX;
        const dy = touches[1].clientY - touches[0].clientY;
        return (Math.atan2(dy, dx) * 180) / Math.PI;
    };

    /**
     * 좌표가 현재 지도의 가시 viewport 안에 있는지 — wrapper 가 141.42% 로 큰 비율이라
     * setBounds 후에도 외곽 마커가 가시 영역 밖에 있을 수 있어, 추가 zoom-out 판단용.
     *
     * bounds.contain() 이 SDK 버전에 따라 undefined 인 경우가 있어 SW/NE 직접 비교로 처리.
     * 이전엔 contain 이 undefined 이면 false 로 떨어져 안전망이 항상 발동 → 과도 축소 회귀.
     */
    const isInsideViewport = (map: any, lat: number, lng: number): boolean => {
        try {
            const viewport: any = map.getBounds?.();
            if (!viewport) return true;
            const sw = viewport.getSouthWest?.();
            const ne = viewport.getNorthEast?.();
            if (!sw || !ne) return true;
            return lat >= sw.getLat() && lat <= ne.getLat()
                && lng >= sw.getLng() && lng <= ne.getLng();
        } catch {
            return true;
        }
    };

    const handleRotateTouchStart = useCallback((e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            rotateBaseRef.current = {
                startAngle: calcAngle(e.touches),
                baseRotation: rotation,
                activated: false,
            };
        }
    }, [rotation]);

    const handleRotateTouchMove = useCallback((e: React.TouchEvent) => {
        if (e.touches.length !== 2 || !rotateBaseRef.current) return;
        const cur = calcAngle(e.touches);
        const ref = rotateBaseRef.current;
        const diff = cur - ref.startAngle;
        // 임계값 미만이면 회전으로 보지 않음 — 카카오맵의 핀치 줌/팬 핸들러에 양보
        if (!ref.activated) {
            if (Math.abs(diff) < ROTATE_THRESHOLD_DEG) return;
            // 임계값 넘은 시점부터 회전 시작 — 점프 방지를 위해 현재 각도를 새 기준으로
            ref.startAngle = cur;
            ref.activated = true;
            setRotating(true);
            return;
        }
        const newDiff = cur - ref.startAngle;
        setRotation(ref.baseRotation + newDiff);
    }, []);

    const handleRotateTouchEnd = useCallback(() => {
        rotateBaseRef.current = null;
        setRotating(false);
    }, []);

    useEffect(() => { latestMarkersRef.current = markers; }, [markers]);

    const handleMyLocation = useCallback(async () => {
        const loc = await fetchLocation(true);
        if (!loc || !mapRef.current) return;
        const pos = new kakao.maps.LatLng(loc.lat, loc.lng);
        mapRef.current.setCenter(pos);
        (mapRef.current as any).setLevel(3, { animate: false });
        // 현재위치 마커 표시
        if (myLocMarkerRef.current) myLocMarkerRef.current.setMap(null);
        myLocMarkerRef.current = new kakao.maps.CustomOverlay({
            position: pos,
            content: myLocationHtml(),
            yAnchor: 0.5,
            map: mapRef.current,
        });
    }, [fetchLocation]);

    // SDK 초기화
    useEffect(() => {
        loadKakaoSDK()
            .then(() => setLoaded(true))
            .catch((e) => setError(e.message));
    }, []);

    // 지도 초기화
    useEffect(() => {
        if (!loaded || !containerRef.current) return;

        const map = new kakao.maps.Map(containerRef.current, {
            center: new kakao.maps.LatLng(center.lat, center.lng),
            level: zoomLevel,
        });

        mapRef.current = map;

        return () => {
            mapRef.current = null;
        };
    }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps

    // 컨테이너 크기가 바뀌면 지도에 알려줍니다.
    // 카카오 지도는 만들어질 때의 픽셀 크기를 그대로 들고 있어서, 폴드를 펴거나
    // 화면을 돌려 컨테이너만 커지면 지도 안쪽이 옛 크기로 남아 어긋납니다.
    // (relayout 뒤에는 중심이 틀어지므로 다시 잡아 줍니다)
    useEffect(() => {
        const el = containerRef.current;
        if (!loaded || !el) return;
        const ro = new ResizeObserver(() => {
            const map = mapRef.current;
            if (!map) return;
            const keep = map.getCenter();
            map.relayout();
            map.setCenter(keep);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [loaded]);

    // 중심 좌표 변경
    useEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.setCenter(new kakao.maps.LatLng(center.lat, center.lng));
    }, [center.lat, center.lng]);

    // 마커 업데이트 — id 키로 재사용, 위치만 setPosition 으로 갱신 (DOM 유지)
    useEffect(() => {
        if (!loaded || !mapRef.current) return;
        const map = mapRef.current;
        const liveIds = new Set<string | number>();

        markers.forEach((m) => {
            liveIds.add(m.id);
            const position = new kakao.maps.LatLng(m.lat, m.lng);
            // 내용(아이콘/색상/라벨) 동일성 해시 — 바뀌면 재생성, 같으면 위치만 업데이트
            // 참여자 tooltip 은 매 프레임 갱신되는 거리(%m) 가 섞여 해시에서 제외 (DOM 유지 → 부드러운 이동).
            // 단, destination tooltip 은 장소명이 바뀌어야 갱신되므로 해시에 포함 — 안 그러면
            // 다른 장소를 선택했을 때 marker 만 이동하고 옛 장소명 말풍선이 그대로 남음.
            const contentHash = m.isDestination
                ? `D|${m.color || ''}|${m.label || ''}|${m.tooltip || ''}`
                : m.rating != null
                    ? `R|${m.rating}|${m.categoryIcon || ''}|${m.color || ''}|${m.tooltip || ''}`
                    : `U|${m.color || ''}|${m.label || ''}|${m.profileImgUrl || ''}`;
            const existing = markerMapRef.current.get(m.id);

            if (existing && existing.contentHash === contentHash) {
                // 내용 동일 — 좌표만 이동. DOM 은 유지되어 카카오 내부 transform 으로 부드럽게 변위
                existing.main.setPosition(position);
                existing.tooltip?.setPosition(position);
                return;
            }

            // 신규 또는 내용 변경 — 기존 제거 후 재생성
            if (existing) {
                existing.main.setMap(null);
                existing.tooltip?.setMap(null);
            }

            let main: kakao.maps.Marker | kakao.maps.CustomOverlay;
            let tooltip: kakao.maps.CustomOverlay | undefined;

            if (m.rating != null && !m.isDestination) {
                // 마커 생김새는 markerStyle 한 곳에서 가져옵니다 — 해외 구글 지도도 같은 것을 씁니다.
                main = new kakao.maps.CustomOverlay({
                    position, content: markerContentHtml(m), yAnchor: 0.5, xAnchor: 0.5, map,
                });
            } else if (m.isDestination) {
                main = new kakao.maps.Marker({ position, map });
                if (m.tooltip) {
                    tooltip = new kakao.maps.CustomOverlay({
                        position, content: destinationTooltipHtml(m.tooltip), yAnchor: 2.2, map,
                    });
                }
            } else {
                main = new kakao.maps.CustomOverlay({
                    position, content: markerContentHtml(m), yAnchor: 1, map,
                });
                // 참여자 마커 위 상시 닉네임 말풍선 — tooltip 필드(닉네임 + 거리) 그대로 사용
                if (m.tooltip) {
                    tooltip = new kakao.maps.CustomOverlay({
                        position, content: memberTooltipHtml(m.tooltip), yAnchor: 2.6, map,
                    });
                }
            }

            markerMapRef.current.set(m.id, { main, tooltip, contentHash });
        });

        // 사라진 id 의 마커만 DOM 에서 제거 (다른 마커는 유지 → 깜빡임 없음)
        Array.from(markerMapRef.current.keys()).forEach(id => {
            if (!liveIds.has(id)) {
                const e = markerMapRef.current.get(id)!;
                e.main.setMap(null);
                e.tooltip?.setMap(null);
                markerMapRef.current.delete(id);
            }
        });
    }, [loaded, markers]);

    // 포커스가 활성 상태면 multi-marker auto-fit 을 전면 중단 — 시간 지나도 자동 복원 안 함.
    // 사용자가 다른 focus 버튼을 누르거나 null 로 명시적으로 해제할 때만 변경.
    const focusActiveRef = useRef<boolean>(false);
    // 포커스 대상이 참여자 마커인 경우 id 저장 — 움직일 때 뷰포트 이탈 시 재중앙정렬
    const focusedMarkerIdRef = useRef<string | number | null>(null);

    // 외부 포커스 요청 — 참여자/목적지 클릭 시 setCenter + setLevel, 이후 auto-fit 완전히 억제
    useEffect(() => {
        if (!loaded || !mapRef.current) return;
        if (!focusTarget) {
            focusActiveRef.current = false;
            focusedMarkerIdRef.current = null;
            return;
        }
        const map = mapRef.current;
        // 즉시 이동 — 카카오맵 setLevel 은 옵션 없으면 부드러운 zoom 애니메이션이 발동해서
        // 사용자에게 &quot;천천히 이동&quot; 처럼 보이므로 { animate: false } 로 명시. setCenter 는
        // 기본 즉시라 별도 옵션 없음.
        map.setCenter(new kakao.maps.LatLng(focusTarget.lat, focusTarget.lng));
        if (typeof focusTarget.level === 'number') {
            (map as any).setLevel(focusTarget.level, { animate: false });
        }
        focusActiveRef.current = true;
        // key 포맷: "p-<id>-<ts>" (참여자) 또는 "dest-<ts>" — dest 는 움직이지 않으므로 추적 대상 아님
        const m = focusTarget.key?.match(/^p-([^-]+)-/);
        focusedMarkerIdRef.current = m ? m[1] : null;
    }, [loaded, focusTarget?.key]); // eslint-disable-line react-hooks/exhaustive-deps

    // '전체 보기' 트리거 — 외부에서 값이 증가할 때마다 모든 마커 기준으로 즉시 fitBounds.
    // 포커스 상태도 함께 해제해 이후 자동 추적이 재개됨.
    useEffect(() => {
        if (!loaded || !mapRef.current || !viewAllTrigger) return;
        focusActiveRef.current = false;
        focusedMarkerIdRef.current = null;
        userInteractingUntilRef.current = 0;
        const ms = latestMarkersRef.current;
        if (!ms || ms.length === 0) return;
        const map = mapRef.current as any;
        // 마커 1개면 bounds 가 0 size 라 카카오가 max level 로 축소. setCenter + level 로 처리.
        if (ms.length === 1) {
            map.setCenter(new kakao.maps.LatLng(ms[0].lat, ms[0].lng));
            // 마커 1개 — UI overlay 영역 고려해 약간 여유 있게 level 5
            map.setLevel(5, { animate: false });
            return;
        }
        const bounds = new kakao.maps.LatLngBounds();
        ms.forEach(m => bounds.extend(new kakao.maps.LatLng(m.lat, m.lng)));
        // padding — wrapper 회전 대비 141.42% 확대 + UI overlay (헤더 ~56px / 하단 시트 / focus 패널) 영역 회피.
        // 상하 140 / 좌우 80 — 모임 상세 진입 시 상단 탭 + 하단 참여자 시트가 마커를 가리던 회귀 보강.
        map.setBounds(bounds, 140, 80, 140, 80);
        // 분포 size 기반 동적 클램프 — 카카오 setBounds 는 padding 보장을 위해 한 단계 더 zoom-out
        // 하는 경향이 있어 좁은 분포에서 과도한 축소가 발생. 분포 위/경도 차이로 적정 max level 산정.
        // (lat/lng diff 1.0 ≈ 100km. 카카오 level: 1 가장 확대 ~ 14 가장 축소)
        const b: any = bounds;
        const sw = b.getSouthWest(), ne = b.getNorthEast();
        const maxDiff = Math.max(Math.abs(ne.getLat() - sw.getLat()), Math.abs(ne.getLng() - sw.getLng()));
        // 분포 size 기반 maxLevel — 거리별로 적절한 zoom 강제.
        // 카카오 setBounds 가 wrapper 141% scale 영향으로 한 단계 더 축소되는 경향이 있어
        // 가까운 거리에서 과도 축소 회귀를 막기 위해 maxLevel 을 명시적으로 적용.
        // 상하 UI overlay (헤더 / 하단 시트 / 포커스 패널) 가려짐 회피용으로 +1 buffer
        // (이전엔 "딱 들어맞는" 수준이라 외곽 마커가 UI 뒤에 숨던 회귀)
        let maxLevel: number;
        if (maxDiff < 0.003) maxLevel = 4;        // ~300m   걸어서 1~2분
        else if (maxDiff < 0.008) maxLevel = 5;   // ~800m   동네 한 블럭
        else if (maxDiff < 0.02) maxLevel = 6;    // ~2km    구 단위
        else if (maxDiff < 0.05) maxLevel = 7;    // ~5km
        else if (maxDiff < 0.15) maxLevel = 8;    // ~15km   시 단위
        else if (maxDiff < 0.5) maxLevel = 9;     // ~50km   광역시
        else maxLevel = 11;                       // 그 이상 (여행)
        const lvl = map.getLevel();
        if (lvl > maxLevel) map.setLevel(maxLevel, { animate: false });
        // 안전망은 maxDiff 가 큰(>0.05) 경우에만 — 가까운 거리에서 발동해 과도 축소 안 되도록.
        // (작은 거리는 이미 maxLevel 로 충분히 보임. 큰 거리에서 wrapper 회전 영향으로 한 단계 더 필요한 케이스만 cover.)
        if (maxDiff >= 0.05) {
            const fullyVisible = ms.every(m => isInsideViewport(map, m.lat, m.lng));
            if (!fullyVisible) {
                const cur = map.getLevel();
                if (cur < 14) map.setLevel(cur + 1, { animate: false });
            }
        }
    }, [loaded, viewAllTrigger]);

    // 사용자 제스처 감지 — wheel/touchstart/mousedown 시 auto-fit 을 10초간 mute
    useEffect(() => {
        const el = containerRef.current;
        if (!loaded || !el) return;
        const mark = () => { userInteractingUntilRef.current = Date.now() + 10000; };
        el.addEventListener('wheel', mark, { passive: true });
        el.addEventListener('touchstart', mark, { passive: true });
        el.addEventListener('mousedown', mark, { passive: true });
        return () => {
            el.removeEventListener('wheel', mark);
            el.removeEventListener('touchstart', mark);
            el.removeEventListener('mousedown', mark);
        };
    }, [loaded]);

    // 주기적 auto-fit — 3초마다 체크하되, 포커스 활성 시 skip. 모든 마커가 이미 뷰포트 안이면 skip.
    // 포커스가 특정 참여자면 그 사람만 추적 — 뷰포트 이탈 직전에만 setCenter (zoom 유지).
    useEffect(() => {
        if (!loaded || !mapRef.current) return;
        let lastFitAt = 0;

        const followFocusedOnce = () => {
            const id = focusedMarkerIdRef.current;
            if (!id) return;
            const ms = latestMarkersRef.current;
            if (!ms) return;
            const target = ms.find(m => String(m.id) === String(id));
            if (!target) return;
            const curBounds: any = (mapRef.current as any).getBounds();
            if (!curBounds) return;
            const sw = curBounds.getSouthWest(), ne = curBounds.getNorthEast();
            // 가장자리 15% 접근 시 재중앙정렬 (zoom 은 유지)
            const latPad = (ne.getLat() - sw.getLat()) * 0.15;
            const lngPad = (ne.getLng() - sw.getLng()) * 0.15;
            const inside = target.lat >= sw.getLat() + latPad && target.lat <= ne.getLat() - latPad &&
                           target.lng >= sw.getLng() + lngPad && target.lng <= ne.getLng() - lngPad;
            if (!inside) {
                // 사용자 제스처 중엔 중앙정렬 안 함 — 사용자 시점 보존
                if (Date.now() <= userInteractingUntilRef.current) return;
                mapRef.current!.setCenter(new kakao.maps.LatLng(target.lat, target.lng));
            }
        };

        const fitAllOnce = () => {
            if (!fitBounds) return;
            if (Date.now() <= userInteractingUntilRef.current) return;
            const ms = latestMarkersRef.current;
            if (!ms || ms.length < 2) return;
            const curBounds: any = (mapRef.current as any).getBounds();
            if (!curBounds) return;
            // 가장자리 10% 접근 시에만 재fit — 이전 25% 는 너무 느슨해 줌아웃 과도.
            const sw = curBounds.getSouthWest(), ne = curBounds.getNorthEast();
            const latPad = (ne.getLat() - sw.getLat()) * 0.10;
            const lngPad = (ne.getLng() - sw.getLng()) * 0.10;
            const allInside = ms.every(m =>
                m.lat >= sw.getLat() + latPad && m.lat <= ne.getLat() - latPad &&
                m.lng >= sw.getLng() + lngPad && m.lng <= ne.getLng() - lngPad
            );
            if (allInside) return;
            if (Date.now() - lastFitAt < 2000) return;
            const bounds = new kakao.maps.LatLngBounds();
            ms.forEach(m => bounds.extend(new kakao.maps.LatLng(m.lat, m.lng)));
            // 외곽 패딩 80px — wrapper 회전 대비 141.42% 영역의 외곽 마진을 가시 영역 안으로 끌어들임.
            const map: any = mapRef.current;
            map.setBounds(bounds, 80, 80, 80, 80);
            // 분포 size 기반 동적 클램프 — viewAllTrigger 와 동일 로직
            const b2: any = bounds;
            const sw2 = b2.getSouthWest(), ne2 = b2.getNorthEast();
            const maxDiff = Math.max(Math.abs(ne2.getLat() - sw2.getLat()), Math.abs(ne2.getLng() - sw2.getLng()));
            let maxLevel: number;
            if (maxDiff < 0.005) maxLevel = 3;
            else if (maxDiff < 0.02) maxLevel = 4;
            else if (maxDiff < 0.05) maxLevel = 5;
            else if (maxDiff < 0.15) maxLevel = 6;
            else if (maxDiff < 0.5) maxLevel = 7;
            else maxLevel = 9;
            const lvl = map.getLevel();
            if (lvl > maxLevel) map.setLevel(maxLevel, { animate: false });
            // 안전망 — 외곽 마커가 가시 viewport 밖에 있으면 한 단계 zoom-out.
            const fullyVisible = ms.every(m => isInsideViewport(map, m.lat, m.lng));
            if (!fullyVisible) {
                const cur = map.getLevel();
                if (cur < 14) map.setLevel(cur + 1, { animate: false });
            }
            lastFitAt = Date.now();
        };

        const tick = () => {
            if (focusActiveRef.current) {
                followFocusedOnce();
            } else {
                fitAllOnce();
            }
        };
        tick();
        const iv = setInterval(tick, 1500);
        return () => clearInterval(iv);
    }, [loaded, fitBounds]);

    // 경로 폴리라인 렌더링
    useEffect(() => {
        if (!loaded || !mapRef.current) return;
        const map = mapRef.current;

        // 기존 폴리라인 제거
        polylinesRef.current.forEach(pl => pl.setMap(null));
        polylinesRef.current = [];

        if (!routes || routes.length === 0) return;

        routes.forEach(route => {
            if (route.coordinates.length < 2) return;
            const path = route.coordinates.map(([lat, lng]) => new kakao.maps.LatLng(lat, lng));
            const polyline = new (kakao.maps as any).Polyline({
                path,
                strokeWeight: route.strokeWidth || 4,
                strokeColor: route.color || '#667eea',
                strokeOpacity: 0.8,
                strokeStyle: 'solid',
            });
            polyline.setMap(map);
            polylinesRef.current.push(polyline);
        });
    }, [loaded, routes]);

    if (error) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#f5f5f5',
                    color: '#999',
                    fontSize: 14,
                }}
            >
                {error}
            </div>
        );
    }

    return (
        <div style={{ position: 'relative', width: '100%', height, overflow: 'hidden' }}>
            <div
                ref={containerRef}
                style={{
                    // 회전 시에도 부모 영역을 항상 덮도록 √2(≈141.42%) 로 확대 + 중앙정렬 음수 오프셋.
                    // 회전 0° 일 땐 잘려서 100% 처럼 보이고, 모든 각도에서 부모 wrapper(overflow:hidden) 가 빈 코너를 가림.
                    position: 'absolute',
                    width: '141.42%',
                    height: '141.42%',
                    top: '-20.71%',
                    left: '-20.71%',
                    transform: `rotate(${rotation}deg)`,
                    transformOrigin: 'center center',
                    transition: rotating ? 'none' : 'transform 0.15s',
                }}
                className="location-map-div"
                onTouchStart={handleRotateTouchStart}
                onTouchMove={handleRotateTouchMove}
                onTouchEnd={handleRotateTouchEnd}
                onTouchCancel={handleRotateTouchEnd}
            />
            {Math.abs(rotation) > 0.5 && (
                <button
                    type="button"
                    onClick={() => setRotation(0)}
                    className={`${ctl['mc-btn']} ${ctl['mc-top-right']}`}
                    title={i18n.t('mapUi.alignNorth')}
                    aria-label={i18n.t('mapUi.alignNorth')}
                >
                    N↑
                </button>
            )}
            {showMyLocationBtn && (
                <button
                    type="button"
                    onClick={handleMyLocation}
                    className={`${ctl['mc-btn']} ${ctl['mc-bottom-right']}`}
                    title={i18n.t('mapUi.currentLocation')}
                    aria-label={i18n.t('mapUi.currentLocation')}
                >
                    <IcPin size={18} />
                </button>
            )}
        </div>
    );
};
